import { Linking } from 'react-native';
import Moment from 'moment';
import { DateRange } from '../redux/dateRangeSlice';
import { UserInstance } from './Types';
import { Area, Floor, Space } from '../components/Map/SetFloorsModal/FloorItem';
import { Building } from '../screens/LocationManagement/components/AddBuildingModal';
import type { ChartDataPoint } from '../graphs/axisUtils';
import { ADDITIONAL_FIELD_TYPES } from '../components/AssetManagement/AddTemplateModal/components/FieldList/components/FieldItem';

export const isSameYear = (date: Date, comparisonDate: Date): boolean => !!date
  && !!comparisonDate && date.getFullYear() === comparisonDate.getFullYear();

export const isSameDate = (date: Date, comparisonDate: Date): boolean => (
  isSameYear(date, comparisonDate)
  && date.getMonth() === comparisonDate.getMonth()
  && date.getDate() === comparisonDate.getDate()
);

export const getRangeHours = (range: DateRange): number => (1 / 3600000)
  * (new Date(range.end).getTime() - new Date(range.start).getTime());

export const getPreviousDateRange = (range: DateRange): DateRange => {
  const end = range.start;
  const start = new Date(2 * new Date(range.start).getTime() - new Date(range.end).getTime())
    .toISOString();
  return { start, end };
};

export const showValueWithHyphenFallback = (value: string): string => (value?.trim().length > 0
  ? value : '-');

export const userFullName = (user: UserInstance): string => (user?.firstName
  ? getUserFullName(user) : user?.email);

export const openInNewTab = (url: string): void => {
  let fixedUrl: string = url;
  if (!fixedUrl.includes('http')) fixedUrl = `http://${fixedUrl}`;
  Linking.openURL(fixedUrl);
};

export const isValidValue = (value: unknown, type: unknown): boolean => Object
  .values(type).includes(value);

export const DEFAULT_TEMPLATE_TITLE = 'Report an issue with {{Asset:Name}} at {{Location:Name}}';

export const getFormattedTemplateTitle = (title: string, assetName: string): string => title
  ?.replace('{{Asset:Name}}', assetName)
  .replace(' at {{Location:Name}}', '')
  || 'Report issue';

export const getFormattedTemplateSubtitle = (subtitle: string): string => subtitle
  ?.replace('{/n}', '\n')?.replace('{\\n}', '\n') || 'What issues do you want to report?';

export const getLocationById = (id: string, buildings: object)
  : null | Building | Floor | Area | Space => {
  let location = null;
  Object.keys(buildings).forEach((buildingId: string) => {
    if (location) return;
    const building: Building = buildings[buildingId];
    if (buildingId === id) location = building;
    else {
      Object.keys(building.floors).forEach((floorId: string) => {
        if (location) return;
        const floor: Floor = building.floors[floorId];
        if (floorId === id) location = floor;
        else {
          Object.keys(floor.areas).forEach((areaId: string) => {
            if (location) return;
            const area: Area = floor.areas[areaId];
            if (areaId === id) location = area;
            else {
              Object.keys(area?.spaces ?? []).forEach((spaceId: string) => {
                if (location) return;
                const space: Space = area.spaces[spaceId];
                if (spaceId === id) location = space;
              });
            }
          });
        }
      });
    }
  });
  return location;
};

export const getLocationHierarchyById = (id: string, buildings: object)
  : { buildingId?: string, floorId?: string, areaId?: string, spaceId?: string } => {
  let found: boolean = false;
  const hierarchy = {
    buildingId: null,
    floorId: null,
    areaId: null,
    spaceId: null,
  };
  Object.keys(buildings).forEach((buildingId: string) => {
    if (found) return;
    const building: Building = buildings[buildingId];
    if (buildingId === id) {
      found = true;
      hierarchy.buildingId = buildingId;
    } else {
      Object.keys(building.floors).forEach((floorId: string) => {
        if (found) return;
        const floor: Floor = building.floors[floorId];
        if (floorId === id) {
          found = true;
          hierarchy.buildingId = buildingId;
          hierarchy.floorId = floorId;
        } else {
          Object.keys(floor.areas).forEach((areaId: string) => {
            if (found) return;
            const area: Area = floor.areas[areaId];
            if (areaId === id) {
              found = true;
              hierarchy.buildingId = buildingId;
              hierarchy.floorId = floorId;
              hierarchy.areaId = areaId;
            } else {
              Object.keys(area?.spaces ?? []).forEach((spaceId: string) => {
                if (found) return;
                if (spaceId === id) {
                  found = true;
                  hierarchy.buildingId = buildingId;
                  hierarchy.floorId = floorId;
                  hierarchy.areaId = areaId;
                  hierarchy.spaceId = spaceId;
                }
              });
            }
          });
        }
      });
    }
  });
  return hierarchy;
};

export const getUserFullName = (user: UserInstance): string | undefined => ((user?.firstName
  || user?.lastName)
  ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim() : user?.email);

export const getDateComponents = (dateAsString: string): number[] => dateAsString
  .replace(' ', '').split('/').map((value: string) => parseInt(value, 10));

export const getFileNameFromPath = (url: string): string => url?.split('/').pop();

export const mapResponseToChartDataPoint = (
  res: { value: number, timestamp: string }[],
): ChartDataPoint[] => res?.map((item: { value: number, timestamp: string }, index: number) => ({
  index,
  date: item.timestamp,
  value: item.value ?? 0,
}));

export const mapBookingsListToBookedTimeData = (
  res: { occurrenceStartDate: string, occurrenceEndDate: string }[],
): ChartDataPoint[] => {
  const hourlyTotals: Record<string, number> = {};
  const addMinutesToHour = (timestamp: number, minutes: number): void => {
    const hourKey = Moment(timestamp).startOf('hour').toISOString();
    hourlyTotals[hourKey] = (hourlyTotals[hourKey] ?? 0) + minutes;
  };

  res.forEach((booking) => {
    let startTime: number = new Date(booking.occurrenceStartDate).getTime();
    const startTimeMinutesRemainder: number = Moment(startTime).diff(
      Moment(startTime).clone().startOf('hour'),
      'minutes',
      true,
    );
    let durationMinutes: number = (new Date(booking.occurrenceEndDate).getTime()
      - startTime) / 60000;

    if (startTimeMinutesRemainder > 0) {
      const firstValue: number = Math.min(60 - startTimeMinutesRemainder, durationMinutes);
      addMinutesToHour(startTime, firstValue);
      durationMinutes -= firstValue;
      startTime += firstValue * 60000;
    }

    while (durationMinutes >= 60) {
      addMinutesToHour(startTime, 60);
      durationMinutes -= 60;
      startTime += 3600000;
    }

    if (durationMinutes > 0) {
      addMinutesToHour(startTime, durationMinutes);
    }
  });

  return Object.keys(hourlyTotals)
    .sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime())
    .map((date: string, index: number) => ({
      index,
      date,
      value: hourlyTotals[date],
    }));
};

export const validateAdditionalFieldValue = (
  value: string,
  format: string,
): boolean => {
  const regex: RegExp = format === ADDITIONAL_FIELD_TYPES.DATE
    ? /^(?:(?:31\/(0[13578]|1[02]))|(?:29|30)\/(0[13-9]|1[0-2]))\/\d{4}$|^(29\/02\/(?:(?:\d{2}(?:0[48]|[2468][048]|[13579][26]))|(?:[02468][048]00|[13579][26]00)))$|^(0[1-9]|1\d|2[0-8])\/(0[1-9]|1[0-2])\/\d{4}$/
    : new RegExp(format || ADDITIONAL_FIELD_TYPES.STANDARD);
  return value.trim().length === 0 || regex.test(value.trim());
};
